'use client';

import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import {
  Popover,
  PopoverButton,
  PopoverPanel,
  Transition,
} from '@headlessui/react';
import { Edit, MoreVertical, Crown, Users, AlertTriangle } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';
import { cn } from '@/lib/utils';
import { playerService } from '@/services/api/players';
import { poolService } from '@/services/api/pools';
import { EditProfileModal } from './EditProfileModal';
import { PoolChangeModal } from './PoolChangeModal';
import { IconAssignmentModal } from './IconAssignmentModal';
import { StatusChangeDialog } from '@/components/tournament/StatusChangeDialog';
import type { Player, Tournament } from '@/lib/types/auction';
import toast from 'react-hot-toast';
import { handleError } from '@/lib/utils/error';

interface PlayerActionsProps {
  player: Player;
  tournament: Tournament | null;
  onAction?: (updatedPlayer: Player | null) => void;
}

interface MenuItemProps {
  onClick: () => void;
  icon: React.ElementType;
  label: string;
  className?: string;
  disabled?: boolean;
}

const MenuItem = ({
  onClick,
  icon: Icon,
  label,
  className = '',
  disabled = false,
}: MenuItemProps) => (
  <button
    onClick={onClick}
    disabled={disabled}
    className={cn(
      'flex w-full items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100',
      disabled && 'cursor-not-allowed opacity-50 hover:bg-transparent',
      className
    )}
  >
    <Icon className="mr-3 h-4 w-4" />
    <span className="flex-1 text-left">{label}</span>
  </button>
);

export function PlayerActions({
  player,
  tournament,
  onAction,
}: PlayerActionsProps) {
  const { user } = useAuth();
  const [pendingAction, setPendingAction] = useState<string | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPoolModal, setShowPoolModal] = useState(false);
  const [showIconModal, setShowIconModal] = useState(false);
  const [isStatusDialogOpen, setIsStatusDialogOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const isMounted = useRef(true);

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  const permissions = useMemo(() => {
    if (!tournament) return null;

    const isTournamentOwner = user?.id && tournament?.owner.id === user?.id;
    const isTeamOwner = user?.id && player.team?.owner?.id === user?.id;
    const isUnverifiedPlayer = !player.user?.emailVerified;
    const isBeforeAuction = ['DRAFT', 'REGISTRATION'].includes(
      tournament?.status
    );
    const isAuctionActive = tournament?.status === 'ACTIVE';
    const isPlayerSold = player.status === 'sold';
    const isIconPlayer = player.role === 'icon';

    return {
      canEdit:
        isTournamentOwner &&
        isUnverifiedPlayer &&
        isBeforeAuction &&
        (!isPlayerSold || isIconPlayer),
      canDelete: isTournamentOwner && isBeforeAuction && !isPlayerSold,
      canChangePool:
        (isTournamentOwner || isTeamOwner) &&
        isBeforeAuction &&
        (!isPlayerSold || isIconPlayer),
      canAssignIcon:
        isTournamentOwner && isBeforeAuction && !isIconPlayer && !isPlayerSold,
      canRemoveIcon: isTournamentOwner && isBeforeAuction && isIconPlayer,
      isInActiveAuction: isAuctionActive,
      isPlayerSold: isPlayerSold,
    };
  }, [tournament, player, user]);

  if (!tournament || !permissions) return null;

  const handleAction = async (action: string) => {
    if (['delete', 'removeIcon'].includes(action)) {
      setPendingAction(action);
      setIsStatusDialogOpen(true);
      return;
    }

    try {
      switch (action) {
        case 'edit':
          setShowEditModal(true);
          break;
        case 'pool':
          setShowPoolModal(true);
          break;
        case 'icon':
          setShowIconModal(true);
          break;
      }
    } catch (error) {
      console.error('Action failed:', error);
    }
  };

  const handleStatusChange = async (action: 'delete' | 'removeIcon') => {
    try {
      setIsLoading(true);

      switch (action) {
        case 'delete':
          await playerService.delete(player.id);
          if (onAction) {
            onAction(null);
          }
          break;
        case 'removeIcon':
          const updatedPlayer = await playerService.removeFromTeam({
            playerId: player.id,
            tournamentId: tournament.id,
            userId: user?.id,
          });
          if (onAction) {
            onAction(updatedPlayer);
          }
          break;
      }

      setIsStatusDialogOpen(false);
      setPendingAction(null);
    } catch (error) {
      toast.error(handleError(error, 'Player action failed'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = async (data: {
    firstName: string;
    lastName: string;
    profilePicture?: string;
    tournamentId: string;
  }) => {
    try {
      const updatedPlayer = await playerService.updateProfile(player.id, data);
      if (onAction) {
        onAction(updatedPlayer);
      }
      setShowEditModal(false);
    } catch (error) {
      throw error;
    }
  };

  const handlePoolChange = async (poolId: string) => {
    try {
      const updatedPlayer = await poolService.assignPoolToPlayer(
        poolId,
        player.id
      );
      if (onAction) {
        onAction(updatedPlayer);
      }
      setShowPoolModal(false);
    } catch (error) {
      throw error;
    }
  };

  const handleIconAssignment = async (data: {
    teamId: string;
    amount: number;
  }) => {
    try {
      const updatedPlayer = await playerService.assignToTeam({
        ...data,
        tournamentId: tournament?.id,
        playerId: player.id,
        userId: user?.id,
      });
      if (onAction) {
        onAction(updatedPlayer);
      }
      setShowIconModal(false);
    } catch (error) {
      throw error;
    }
  };

  const getAvailableActions = () => {
    const actions = [];

    // Edit Profile - Allow for icon players in draft/registration phase
    if (permissions.canEdit && !permissions.isInActiveAuction) {
      actions.push({
        type: 'edit',
        component: (
          <MenuItem
            onClick={() => handleAction('edit')}
            icon={Edit}
            label="Edit Profile"
          />
        ),
      });
    }

    // Change Pool - Allow for icon players in draft/registration phase
    if (permissions.canChangePool && !permissions.isInActiveAuction) {
      actions.push({
        type: 'pool',
        component: (
          <MenuItem
            onClick={() => handleAction('pool')}
            icon={Users}
            label="Change Pool"
          />
        ),
      });
    }

    // Icon Player Actions - Only before auction and if not already an icon
    if (permissions.canAssignIcon && !permissions.isInActiveAuction) {
      actions.push({
        type: 'icon',
        component: (
          <MenuItem
            onClick={() => handleAction('icon')}
            icon={Crown}
            label="Assign as Icon Player"
          />
        ),
      });
    }

    // Remove Icon Status - Only for icon players in draft/registration phase
    if (permissions.canRemoveIcon && !permissions.isInActiveAuction) {
      actions.push({
        type: 'removeIcon',
        component: (
          <MenuItem
            onClick={() => handleAction('removeIcon')}
            icon={Crown}
            label="Remove Icon Status"
            className="text-amber-600"
          />
        ),
      });
    }

    // Delete Player - Only before auction and if not sold
    // if (permissions.canDelete && !permissions.isInActiveAuction) {
    //   actions.push({
    //     type: 'delete',
    //     component: (
    //       <MenuItem
    //         onClick={() => handleAction('delete')}
    //         icon={Trash}
    //         label="Delete Player"
    //         className="text-red-700"
    //       />
    //     ),
    //   });
    // }

    return actions;
  };

  const availableActions = getAvailableActions();

  // Don't render anything if no actions are available
  if (availableActions.length === 0) {
    return null;
  }

  const primaryActions = availableActions.filter(
    (a) => !['delete', 'removeIcon'].includes(a.type)
  );
  const destructiveActions = availableActions.filter((a) =>
    ['delete', 'removeIcon'].includes(a.type)
  );

  const showUnverifiedWarning =
    !player.user?.emailVerified && tournament?.owner.id === user?.id;

  const getStatusDialogContent = () => {
    if (!pendingAction) return null;

    switch (pendingAction) {
      case 'delete':
        return {
          title: 'Delete Player',
          description: 'Are you sure you want to delete this player?',
          impacts: [
            'Player will be permanently removed',
            'This action cannot be undone',
            'All associated data will be deleted',
          ],
          validations: [
            {
              label: 'Player Status',
              status:
                player.status === 'sold'
                  ? ('error' as const)
                  : ('success' as const),
              message:
                player.status === 'sold'
                  ? 'Cannot delete sold player'
                  : 'Player can be deleted',
            },
          ],
          warningLevel: 'danger' as const,
        };
      case 'removeIcon':
        return {
          title: 'Remove Icon Status',
          description:
            "Are you sure you want to remove this player's icon status?",
          impacts: [
            'Player will be unassigned from their team',
            'Icon status will be removed',
            'Team budget will be adjusted',
          ],
          validations: [
            {
              label: 'Icon Status',
              status:
                player.role === 'icon'
                  ? ('success' as const)
                  : ('error' as const),
              message:
                player.role === 'icon'
                  ? 'Player is an icon'
                  : 'Player is not an icon',
            },
          ],
          warningLevel: 'warning' as const,
        };
      default:
        return null;
    }
  };

  const statusContent = getStatusDialogContent();

  return (
    <>
      <div className="relative">
        <Popover className="relative">
          {({ open, close }) => (
            <>
              <PopoverButton
                onClick={(e) => {
                  e.stopPropagation();
                }}
                className="inline-flex items-center rounded-full p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
              >
                <MoreVertical className="h-4 w-4" />
              </PopoverButton>

              <Transition
                as={Fragment}
                show={open}
                enter="transition ease-out duration-100"
                enterFrom="transform opacity-0 scale-95"
                enterTo="transform opacity-100 scale-100"
                leave="transition ease-in duration-75"
                leaveFrom="transform opacity-100 scale-100"
                leaveTo="transform opacity-0 scale-95"
                afterLeave={() => {
                  // Clean up after transition completes
                  if (isMounted.current) {
                    // Single cleanup on unmount
                  }
                }}
              >
                <PopoverPanel
                  className="absolute right-0 top-full z-40 mt-2 w-56 rounded-lg bg-white shadow-xl ring-1 ring-black ring-opacity-5 focus:outline-none"
                  onClick={(e) => {
                    e.stopPropagation(); // Stop event from bubbling to card
                  }}
                >
                  {showUnverifiedWarning && (
                    <div className="border-b border-gray-100 px-4 py-2">
                      <div className="flex items-center gap-2 text-xs text-amber-600">
                        <AlertTriangle className="h-4 w-4" />
                        <span>Email not verified</span>
                      </div>
                    </div>
                  )}

                  {primaryActions.length > 0 && (
                    <div className="py-1">
                      {primaryActions.map((a, i) => (
                        <div
                          key={i}
                          onClick={(e) => {
                            e.stopPropagation(); // Stop event from bubbling to card
                            close();
                            a.component.props.onClick();
                          }}
                        >
                          {a.component}
                        </div>
                      ))}
                    </div>
                  )}

                  {destructiveActions.length > 0 && (
                    <div className="border-t border-gray-100 py-1">
                      {destructiveActions.map((a, i) => (
                        <div
                          key={i}
                          onClick={(e) => {
                            e.stopPropagation(); // Stop event from bubbling to card
                            close();
                            a.component.props.onClick();
                          }}
                        >
                          {a.component}
                        </div>
                      ))}
                    </div>
                  )}
                </PopoverPanel>
              </Transition>
            </>
          )}
        </Popover>

        {/* Only render modals when they are needed */}
        {showEditModal && permissions.canEdit && (
          <EditProfileModal
            player={player}
            tournament={tournament}
            isOpen={showEditModal}
            onClose={() => setShowEditModal(false)}
            onSave={handleEdit}
          />
        )}

        {showPoolModal && permissions.canChangePool && (
          <PoolChangeModal
            player={player}
            availablePools={tournament?.pools}
            isOpen={showPoolModal}
            onClose={() => setShowPoolModal(false)}
            onSave={handlePoolChange}
          />
        )}

        {showIconModal && permissions.canAssignIcon && (
          <IconAssignmentModal
            teams={tournament?.teams}
            isOpen={showIconModal}
            onClose={() => setShowIconModal(false)}
            onSave={handleIconAssignment}
          />
        )}

        {statusContent && (
          <StatusChangeDialog
            isOpen={isStatusDialogOpen}
            onClose={() => {
              setIsStatusDialogOpen(false);
              setPendingAction(null);
            }}
            onConfirm={() =>
              handleStatusChange(pendingAction as 'delete' | 'removeIcon')
            }
            title={statusContent.title}
            description={statusContent.description}
            impacts={statusContent.impacts}
            validations={statusContent.validations}
            warningLevel={statusContent.warningLevel}
            isLoading={isLoading}
          />
        )}
      </div>
    </>
  );
}
